/**
 * Admin Promo Code Management Page
 *
 * CRUD interface for managing promotional codes.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Tag,
  Eye,
  EyeOff,
  X,
  ArrowLeft,
  Calendar,
  Percent,
  DollarSign,
  Gift,
  Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import Button from '../../../../components/base/Button';
import adminService, {
  type PromoCode,
  type CreatePromoCodeParams,
} from '../../../../services/adminService';

// ============================================================================
// PROMO CODE FORM MODAL
// ============================================================================

function PromoCodeFormModal({
  promoCode,
  onSave,
  onClose,
  isLoading,
}: {
  promoCode?: PromoCode;
  onSave: (data: CreatePromoCodeParams) => void;
  onClose: () => void;
  isLoading: boolean;
}) {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const isEdit = !!promoCode;

  const [formData, setFormData] = useState<CreatePromoCodeParams>({
    code: promoCode?.code || '',
    description: promoCode?.description || '',
    descriptionAr: promoCode?.descriptionAr || '',
    type: promoCode?.type || 'PERCENTAGE',
    value: promoCode?.value || 0,
    maxUses: promoCode?.maxUses || undefined,
    maxUsesPerUser: promoCode?.maxUsesPerUser || 1,
    validFrom: promoCode?.validFrom
      ? new Date(promoCode.validFrom).toISOString().slice(0, 16)
      : new Date().toISOString().slice(0, 16),
    validUntil: promoCode?.validUntil
      ? new Date(promoCode.validUntil).toISOString().slice(0, 16)
      : '',
    isActive: promoCode?.isActive ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      validFrom: formData.validFrom ? new Date(formData.validFrom).toISOString() : undefined,
      validUntil: formData.validUntil ? new Date(formData.validUntil).toISOString() : undefined,
      maxUses: formData.maxUses || undefined,
    };
    onSave(submitData);
  };

  const typeOptions = [
    { value: 'PERCENTAGE', label: 'Percentage Discount', icon: Percent },
    { value: 'FIXED_AMOUNT', label: 'Fixed Amount', icon: DollarSign },
    { value: 'FREE_TRIAL', label: 'Free Trial', icon: Gift },
    { value: 'FEATURE_UNLOCK', label: 'Feature Unlock', icon: Sparkles },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">
            {isEdit ? 'Edit Promo Code' : 'Create Promo Code'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Code */}
          <div>
            <label className="block text-sm font-medium mb-2">Promo Code</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) =>
                setFormData({ ...formData, code: e.target.value.toUpperCase() })
              }
              className="w-full px-4 py-2 rounded-lg border font-mono uppercase dark:bg-gray-700 dark:border-gray-600"
              placeholder="e.g., SUMMER2024"
              required
              disabled={isEdit}
            />
            {!isEdit && (
              <p className="text-xs text-gray-500 mt-1">
                Use letters, numbers, underscores, or dashes. Cannot be changed later.
              </p>
            )}
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium mb-2">Discount Type</label>
            <div className="grid grid-cols-2 gap-3">
              {typeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      type: option.value as CreatePromoCodeParams['type'],
                    })
                  }
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-colors ${
                    formData.type === option.value
                      ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/30'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                  }`}
                >
                  <option.icon
                    className={`w-5 h-5 ${
                      formData.type === option.value ? 'text-[#004D40]' : 'text-gray-400'
                    }`}
                  />
                  <span
                    className={`text-sm ${
                      formData.type === option.value ? 'text-[#003930] font-medium' : ''
                    }`}
                  >
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Value */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {formData.type === 'PERCENTAGE' ? 'Discount Percentage' : 'Value (SAR)'}
            </label>
            <div className="relative">
              <input
                type="number"
                value={formData.value}
                onChange={(e) =>
                  setFormData({ ...formData, value: parseInt(e.target.value) || 0 })
                }
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                min="0"
                max={formData.type === 'PERCENTAGE' ? 100 : undefined}
                required
              />
              {formData.type === 'PERCENTAGE' && (
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                  %
                </span>
              )}
            </div>
          </div>

          {/* Usage Limits */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Max Total Uses</label>
              <input
                type="number"
                value={formData.maxUses || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    maxUses: e.target.value ? parseInt(e.target.value) : undefined,
                  })
                }
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                min="1"
                placeholder="Unlimited"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Max Uses Per User</label>
              <input
                type="number"
                value={formData.maxUsesPerUser}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    maxUsesPerUser: parseInt(e.target.value) || 1,
                  })
                }
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                min="1"
                required
              />
            </div>
          </div>

          {/* Validity Period */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Valid From</label>
              <input
                type="datetime-local"
                value={formData.validFrom}
                onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Valid Until</label>
              <input
                type="datetime-local"
                value={formData.validUntil}
                onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                placeholder="No expiry"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty for no expiry</p>
            </div>
          </div>

          {/* Description */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Description (English)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border resize-none dark:bg-gray-700 dark:border-gray-600"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description (Arabic)</label>
              <textarea
                value={formData.descriptionAr}
                onChange={(e) =>
                  setFormData({ ...formData, descriptionAr: e.target.value })
                }
                className="w-full px-4 py-2 rounded-lg border resize-none dark:bg-gray-700 dark:border-gray-600"
                rows={2}
                dir="rtl"
              />
            </div>
          </div>

          {/* Active Status */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-4 h-4 rounded text-[#004D40]"
            />
            <span className="text-sm">Active (can be used)</span>
          </label>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : isEdit ? (
                'Update Code'
              ) : (
                'Create Code'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function AdminPromoCodesPage() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingCode, setEditingCode] = useState<PromoCode | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch promo codes
  const fetchPromoCodes = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await adminService.getPromoCodes({ pageSize: 100 });
      setPromoCodes(response.data);
    } catch (err) {
      console.error('Failed to fetch promo codes:', err);
      setError('Failed to load promo codes');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPromoCodes();
  }, [fetchPromoCodes]);

  // Create promo code
  const handleCreate = async (data: CreatePromoCodeParams) => {
    try {
      setIsSaving(true);
      setError(null);
      await adminService.createPromoCode(data);
      setShowCreateModal(false);
      fetchPromoCodes();
    } catch (err: unknown) {
      console.error('Failed to create promo code:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to create promo code';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // Update promo code
  const handleUpdate = async (data: CreatePromoCodeParams) => {
    if (!editingCode) return;
    try {
      setIsSaving(true);
      setError(null);
      // Remove code from update payload (cannot update code)
      const { code: _, ...updateData } = data;
      await adminService.updatePromoCode(editingCode.id, updateData);
      setEditingCode(null);
      fetchPromoCodes();
    } catch (err: unknown) {
      console.error('Failed to update promo code:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to update promo code';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // Delete promo code
  const handleDelete = async (promoCode: PromoCode) => {
    if (!confirm(`Are you sure you want to deactivate "${promoCode.code}"?`)) return;
    try {
      setError(null);
      await adminService.deletePromoCode(promoCode.id);
      fetchPromoCodes();
    } catch (err) {
      console.error('Failed to delete promo code:', err);
      setError('Failed to deactivate promo code');
    }
  };

  // Type badge
  const getTypeBadge = (type: PromoCode['type']) => {
    const badges: Record<
      PromoCode['type'],
      { label: string; color: string; icon: React.ElementType }
    > = {
      PERCENTAGE: { label: 'Percentage', color: 'bg-blue-100 text-blue-700', icon: Percent },
      FIXED_AMOUNT: { label: 'Fixed', color: 'bg-green-100 text-green-700', icon: DollarSign },
      FREE_TRIAL: { label: 'Free Trial', color: 'bg-[var(--pv-color-surface-sunken)] text-[#003930]', icon: Gift },
      FEATURE_UNLOCK: {
        label: 'Feature',
        color: 'bg-amber-100 text-amber-700',
        icon: Sparkles,
      },
    };
    const badge = badges[type];
    const Icon = badge.icon;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}
      >
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  // Expiry status
  const getExpiryStatus = (validUntil?: string) => {
    if (!validUntil) return null;
    const expiry = new Date(validUntil);
    const now = new Date();
    if (expiry < now) {
      return <span className="text-red-600 text-xs">Expired</span>;
    }
    const daysLeft = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 7) {
      return <span className="text-amber-600 text-xs">Expires in {daysLeft}d</span>;
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 p-8 flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#004D40]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white">
                Promo Codes
              </h1>
              <p className="text-gray-500">Manage promotional codes and discounts</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/subscriptions/analytics">
              <Button variant="outline">View Analytics</Button>
            </Link>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Code
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
            {error}
          </div>
        )}

        {/* Promo Codes Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                  Code
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                  Value
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                  Usage
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                  Validity
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-gray-700">
              {promoCodes.map((code) => (
                <tr
                  key={code.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-[#004D40]" />
                      <code className="font-mono font-medium text-gray-900 dark:text-white">
                        {code.code}
                      </code>
                    </div>
                    {code.description && (
                      <p className="text-xs text-gray-500 mt-1 truncate max-w-xs">
                        {code.description}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">{getTypeBadge(code.type)}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {code.type === 'PERCENTAGE'
                      ? `${code.value}%`
                      : code.type === 'FREE_TRIAL'
                        ? `${code.value} days`
                        : `${code.value} SAR`}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    <span className="font-medium">{code.currentUses}</span>
                    {code.maxUses && (
                      <span className="text-gray-500"> / {code.maxUses}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <div className="text-sm">
                        {code.validUntil ? (
                          <div>
                            <span className="text-gray-700 dark:text-gray-300">
                              {format(new Date(code.validUntil), 'MMM d, yyyy')}
                            </span>
                            <div>{getExpiryStatus(code.validUntil)}</div>
                          </div>
                        ) : (
                          <span className="text-gray-500">No expiry</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {code.isActive ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <Eye className="w-4 h-4" /> Active
                      </span>
                    ) : (
                      <span className="text-gray-400 flex items-center gap-1">
                        <EyeOff className="w-4 h-4" /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setEditingCode(code)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg mr-1"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    </button>
                    <button
                      onClick={() => handleDelete(code)}
                      className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 rounded-lg"
                      title="Deactivate"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {promoCodes.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    No promo codes yet. Create one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <PromoCodeFormModal
          onSave={handleCreate}
          onClose={() => setShowCreateModal(false)}
          isLoading={isSaving}
        />
      )}

      {/* Edit Modal */}
      {editingCode && (
        <PromoCodeFormModal
          promoCode={editingCode}
          onSave={handleUpdate}
          onClose={() => setEditingCode(null)}
          isLoading={isSaving}
        />
      )}
    </div>
  );
}
